// Tickets (issue tracker) : hand-written axios client (kept out of the regenerated src/API/). All
// calls go through the shared axios instance so the Auth0 bearer interceptor applies on deployed.
import axios from "axios";

const apiBase = (process.env.REACT_APP_API_URL ?? "").replace(/\/+$/, "");

/** Resolved author identity for display: real name + picture + a profile id (Auth0 sub or local id)
 *  so the UI can render an avatar and a clickable /account/{id} link instead of a raw email/"SYSTEM". */
export interface TicketAuthor {
	id?: string | null;
	name: string;
	email?: string | null;
	pictureUrl?: string | null;
	/** Storage key of the avatar uploaded in the admin : wins over `pictureUrl`. */
	avatarKey?: string | null;
}

/** A content document linked to a ticket: admin id + schema key + a display title captured at link time. */
export interface TicketLinkedContent {
	contentId: string;
	contentType: string;
	title?: string | null;
}

export interface Ticket {
	id: string;
	title: string;
	description: string;
	category: string;
	status: "open" | "closed";
	linkedContentId?: string | null;
	linkedContentType?: string | null;
	linkedContents?: TicketLinkedContent[];
	createdByEmail: string;
	createdAt: string;
	updatedAt: string;
	closedAt?: string | null;
	closedByEmail?: string | null;
	assignees: string[];
	commentCount: number;
	createdBy?: TicketAuthor;
	closedBy?: TicketAuthor | null;
	assigneesResolved?: TicketAuthor[];
}

export interface TicketComment {
	id: number;
	authorEmail: string;
	body: string;
	createdAt: string;
	author?: TicketAuthor;
}

export interface TicketCategory {
	key: string;
	label: string;
}

export interface TicketListResult {
	items: Ticket[];
	total: number;
	page: number;
	pageSize: number;
}

export interface TicketFilters {
	status?: string;
	category?: string;
	assignee?: string;
	linkedContentId?: string;
	mine?: boolean;
	page?: number;
	pageSize?: number;
}

export interface CreateTicketInput {
	title: string;
	description?: string;
	category?: string;
	linkedContentId?: string | null;
	linkedContentType?: string | null;
	linkedContents?: TicketLinkedContent[];
	assignees?: string[];
}

async function get<T>(path: string, params?: any): Promise<T> {
	const r = await axios.get<T>(`${apiBase}/api/tickets${path}`, { params, withCredentials: true });
	return r.data;
}

export const ticketsClient = {
	list: (filters: TicketFilters = {}) => get<TicketListResult>("", filters),
	get: (id: string) => get<{ ticket: Ticket; comments: TicketComment[] }>(`/${encodeURIComponent(id)}`),
	categories: () => get<TicketCategory[]>("/categories"),
	myOpenCount: () => get<{ count: number }>("/my-open-count").then((r) => r.count),
	defaultAssignees: () => get<Array<{ category: string; email: string }>>("/default-assignees"),

	create: async (input: CreateTicketInput): Promise<Ticket> => {
		const r = await axios.post<Ticket>(`${apiBase}/api/tickets`, input, { withCredentials: true });
		return r.data;
	},
	comment: (id: string, body: string) =>
		axios.post(`${apiBase}/api/tickets/${encodeURIComponent(id)}/comments`, { body }, { withCredentials: true }),
	close: (id: string) => axios.post(`${apiBase}/api/tickets/${encodeURIComponent(id)}/close`, {}, { withCredentials: true }),
	reopen: (id: string) => axios.post(`${apiBase}/api/tickets/${encodeURIComponent(id)}/reopen`, {}, { withCredentials: true }),
	setAssignees: (id: string, assignees: string[]) =>
		axios.put(`${apiBase}/api/tickets/${encodeURIComponent(id)}/assignees`, { assignees }, { withCredentials: true }),
	setLinkedContents: (id: string, contents: TicketLinkedContent[]) =>
		axios.put(`${apiBase}/api/tickets/${encodeURIComponent(id)}/linked-contents`, { contents }, { withCredentials: true }),
	setCategoryDefaults: (category: string, assignees: string[]) =>
		axios.put(`${apiBase}/api/tickets/default-assignees/${encodeURIComponent(category)}`, { assignees }, { withCredentials: true }),
	remove: (id: string) => axios.delete(`${apiBase}/api/tickets/${encodeURIComponent(id)}`, { withCredentials: true })
};
